import { useEffect, useState, useCallback, useRef } from 'react';
import { APP_VERSION } from '@/version';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CHECK_INTERVAL_MS = 5 * 60 * 1000; // check every 5 min
const HEALED_KEY = 'compass_reloaded_for_version';

// Session 296b — a banner only helps a reader who notices it. Twice in a row a
// user reported bugs that had already been fixed, from a tab holding an old
// bundle. So reload for them, unless they're mid-input or mid-dialog.
const selfHeal = (serverVersion) => {
  // One reload per server version per tab: if the two versions ever disagree
  // permanently, this must not become a reload loop.
  if (sessionStorage.getItem(HEALED_KEY) === serverVersion) return;
  const active = document.activeElement;
  if (active && ['INPUT', 'TEXTAREA', 'SELECT'].includes(active.tagName)) return;
  if (active && active.isContentEditable) return;
  if (document.querySelector('[role="dialog"]')) return;
  sessionStorage.setItem(HEALED_KEY, serverVersion);
  window.location.reload();
};

export const useVersionCheck = () => {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const swWaitingRef = useRef(null);

  // ── API version check ──────────────────────────────────────────────────────
  const checkApiVersion = useCallback(async () => {
    // Session 44 — Also proactively ask the browser to refetch sw.js.
    // Without this, a stuck-on-old-SW client (whose 24-hour SW
    // freshness check hasn't fired yet) would never pick up the
    // network-first chunk strategy and stay frozen on cached chunks
    // — exactly what the "missing Upload field type" bug looked like.
    if ('serviceWorker' in navigator) {
      try {
        const reg = await navigator.serviceWorker.getRegistration();
        if (reg) reg.update();   // browser bypasses SW for sw.js itself
      } catch { /* offline — try again next tick */ }
    }
    try {
      const res = await fetch(`${API}/version`, { cache: 'no-store' });
      if (!res.ok) return;
      const { version } = await res.json();
      if (version && version !== APP_VERSION) {
        setUpdateAvailable(true);
        selfHeal(version);
      }
    } catch {
      // Offline or server unavailable — ignore silently
    }
  }, []);

  // ── Apply update ───────────────────────────────────────────────────────────
  const applyUpdate = useCallback(() => {
    if (swWaitingRef.current) {
      swWaitingRef.current.postMessage({ type: 'SKIP_WAITING' });
    } else {
      window.location.reload();
    }
  }, []);

  useEffect(() => {
    // ── Service Worker update listener ─────────────────────────────────────
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then((reg) => {
        const handleUpdateFound = () => {
          const newSW = reg.installing;
          if (!newSW) return;
          newSW.addEventListener('statechange', () => {
            if (newSW.state === 'installed' && navigator.serviceWorker.controller) {
              swWaitingRef.current = newSW;
              setUpdateAvailable(true);
            }
          });
        };

        reg.addEventListener('updatefound', handleUpdateFound);
        // Trigger a check for an already-waiting SW
        if (reg.waiting && navigator.serviceWorker.controller) {
          swWaitingRef.current = reg.waiting;
          setUpdateAvailable(true);
        }
      });

      // When SW controller changes (skipWaiting applied), reload only if not in active form interaction.
      // The FIRST install's clients.claim() is not an update — reloading then
      // drops deep-link params pages have already stripped (?wizard_id=…).
      let hadController = !!navigator.serviceWorker.controller;
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (!hadController) { hadController = true; return; }
        const active = document.activeElement;
        const inForm = active && (
          active.tagName === 'INPUT' ||
          active.tagName === 'TEXTAREA' ||
          active.tagName === 'SELECT'
        );
        if (!inForm) {
          window.location.reload();
        }
        // If user is filling a form, the update banner is already shown — they can reload manually
      });
    }

    // ── API version checks ─────────────────────────────────────────────────
    checkApiVersion();
    const interval = setInterval(checkApiVersion, CHECK_INTERVAL_MS);
    window.addEventListener('focus', checkApiVersion);
    window.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') checkApiVersion();
    });

    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', checkApiVersion);
    };
  }, [checkApiVersion]);

  return { updateAvailable, applyUpdate };
};
